import { useEffect, useRef, useState } from 'react';

// Custom hook for scroll-triggered animations
export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
  } = options;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            setHasAnimated(true);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isVisible || hasAnimated];
};

// Animation variants
export const animations = {
  fadeInUp: {
    hidden: {
      opacity: 0,
      transform: 'translateY(60px)',
    },
    visible: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  fadeInDown: {
    hidden: {
      opacity: 0,
      transform: 'translateY(-60px)',
    },
    visible: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  fadeInLeft: {
    hidden: {
      opacity: 0,
      transform: 'translateX(-60px)',
    },
    visible: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  fadeInRight: {
    hidden: {
      opacity: 0,
      transform: 'translateX(60px)',
    },
    visible: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  scaleIn: {
    hidden: {
      opacity: 0,
      transform: 'scale(0.8)',
    },
    visible: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
  rotateIn: {
    hidden: {
      opacity: 0,
      transform: 'rotate(-10deg) scale(0.9)',
    },
    visible: {
      opacity: 1,
      transform: 'rotate(0) scale(1)',
    },
  },
};

export default useScrollAnimation;
